import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Box,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../api/client.js';
import { formatDateTime } from '../lib/format.js';
import { useT } from '../i18n/useT.js';

/** Drill-down query params — every criterion optional, forwarded to the API. */
export interface DrilldownParams {
  days: number;
  version?: string;
  device?: string;
  projectId?: string;
  dow?: number;
  hour?: number;
  path?: string;
  hasError?: boolean;
  hasRageClick?: boolean;
  hasDeadClick?: boolean;
  minFrustration?: number;
  maxFrustration?: number;
  userEmail?: string;
  userId?: string;
  entryPath?: string;
  frictionPath?: string;
  trafficSource?: string;
}

interface DrilldownSession {
  id: string;
  projectId: string;
  userEmail: string | null;
  userId: string | null;
  anonymousId: string;
  startedAt: string;
  durationMs: number;
  errorCount: number;
  rageClickCount: number;
  frustrationScore: number;
  lastUrl: string;
}

const fmtDur = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const who = (s: DrilldownSession) => s.userEmail || s.userId || `anon ${s.anonymousId.slice(0, 8)}`;
const pathOf = (url: string) => {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
};

/**
 * A popup listing the sessions behind a clicked Support Analytics chart value.
 * Each row links to the session replay. `params` carries the drill-down criteria
 * (plus the page's active version/segment/project filters).
 */
export function SessionDrilldownDialog({
  open,
  onClose,
  orgId,
  title,
  subtitle,
  params,
}: {
  open: boolean;
  onClose: () => void;
  orgId?: string;
  title: string;
  subtitle?: string;
  params: DrilldownParams;
}) {
  const t = useT().replay.drilldown.columns;
  const q = useQuery({
    enabled: open && !!orgId,
    queryKey: ['drilldown', orgId, params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
      return (await api.get(`/v1/orgs/${orgId}/insights/drilldown?${qs.toString()}`)).data as DrilldownSession[];
    },
  });
  const rows = q.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="span">{title}</Typography>
          {!q.isLoading && <Chip size="small" label={`${rows.length} session${rows.length === 1 ? '' : 's'}`} />}
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowX: 'auto' }}>
        {q.isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No sessions match this selection.
          </Typography>
        ) : (
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t.user}</TableCell>
                <TableCell>{t.page}</TableCell>
                <TableCell align="right">{t.duration}</TableCell>
                <TableCell align="right">{t.errors}</TableCell>
                <TableCell align="right">{t.frustration}</TableCell>
                <TableCell>{t.started}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/projects/${s.projectId}/sessions/${s.id}`} onClick={onClose} underline="hover">
                      {who(s)}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                    {pathOf(s.lastUrl)}
                  </TableCell>
                  <TableCell align="right">{fmtDur(s.durationMs)}</TableCell>
                  <TableCell align="right" sx={{ color: s.errorCount ? '#dc2626' : 'text.disabled' }}>{s.errorCount}</TableCell>
                  <TableCell align="right" sx={{ color: s.frustrationScore >= 40 ? '#f59e0b' : 'text.disabled' }}>{s.frustrationScore}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.startedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
